import { convertToWebP } from "../imageUtils";
import { getTemplateDeliveryBudget } from "../templateImageDelivery";

/**
 * Feed tiles are ~140–280px wide (2x ≈ 560). Ask Supabase for a display-sized
 * WebP instead of the original. Non-Supabase URLs (Instagram CDN, local
 * assets) cannot be resized here and are returned unchanged.
 *
 * AVIF is supported by the same render API, but a second cold transform
 * delays the first paint. WebP at the right width is the win; AVIF can wait
 * until transforms are warmed at upload time.
 */
export const FEED_CARD_SIZES = "(max-width: 700px) 46vw, 280px";

export type FeedCardImage = {
  /** Display URL (WebP + width when Supabase can transform). */
  src: string;
  srcSet?: string;
  sizes?: string;
  /** Original URL used as the `<img>` fallback inside `<picture>`. */
  fallbackSrc: string;
};

function alreadyTransformed(url: string): boolean {
  return /[?&]format=(webp|avif)\b/i.test(url);
}

function canTransform(url: string): boolean {
  return (
    url.includes("/storage/v1/object/public/") ||
    url.includes("/storage/v1/render/image/")
  );
}

export function resolveFeedCardImage(
  url: string,
  priority = false,
): FeedCardImage {
  const raw = String(url || "").trim();
  if (!raw) return { src: "", fallbackSrc: "" };

  if (
    raw.startsWith("data:") ||
    raw.startsWith("blob:") ||
    raw.startsWith("/") ||
    alreadyTransformed(raw) ||
    !canTransform(raw)
  ) {
    return { src: raw, fallbackSrc: raw };
  }

  const budget = getTemplateDeliveryBudget();
  // First screen can be a bit larger; the rest stay at the grid-tile budget.
  const width = priority
    ? Math.min(960, Math.max(budget.lcpWidth, 640))
    : budget.shopWidth;
  const retina = Math.min(1200, width * 2);
  const quality = budget.quality;
  const src = convertToWebP(raw, width, undefined, quality);
  const retinaSrc = convertToWebP(raw, retina, undefined, quality);
  return {
    src,
    srcSet: `${src} ${width}w, ${retinaSrc} ${retina}w`,
    sizes: FEED_CARD_SIZES,
    fallbackSrc: raw,
  };
}
